import { pluralize } from '@art-suite/art-core-ts';
import type { ApiHeartbeatDetails } from '@kaiizen/kai-pro-api';
import { useHeartbeatDetails } from '@kaiizen/kai-pro-api-react-state';

import { CommonActionButton } from '@kaiizen/kai-shiki/react';

import { DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES, SuperAdminToolsPanelStyles } from './superAdminToolsPanelStyles';
import { SuperAdminToolsRow, SuperAdminToolsRows } from './SuperAdminToolsRows';

/** `0.99876` -> `99.88%` */
const formatUptimePercent = (uptime: number) => `${(uptime * 100).toFixed(2)}%`;

/** Unix seconds -> the viewer's local date and time. */
const formatUnixSecondsAsLocalTime = (seconds: number) => new Date(seconds * 1000).toLocaleString();

/** `0.01234` -> `$0.0123`: heartbeat LLM pings cost fractions of a cent. */
const formatLlmCostUsd = (amount: number) => `$${amount.toFixed(4)}`;

const getServerRows = (details: ApiHeartbeatDetails): SuperAdminToolsRow[] => [
  { label: 'Environment', value: details.ENV },
  { label: 'Version', value: details.version },
  { label: 'Commit', value: details.commit ?? '--' },
  { label: 'Built', value: details.built ?? '--' },
  { label: 'Running for', value: `${details.timeSinceLastStart} (since ${formatUnixSecondsAsLocalTime(details.startedAt)})` },
  { label: 'Uptime', value: formatUptimePercent(details.uptime) },
  { label: 'Checks', value: `${details.okCount} ok, ${details.failingCount} failing, ${details.totalCount} total` },
  {
    label: 'Background checks',
    value: details.HEARTBEAT_ENABLED ? `every ${details.REFRESH_INTERVAL_SECONDS}s` : 'off (checks run only when this view loads)',
  },
  { label: 'Heartbeat LLM cost', value: formatLlmCostUsd(details.heartbeatGptCostsSinceLastStart.totalCost ?? 0) },
];

const getServiceRows = (details: ApiHeartbeatDetails, styles: SuperAdminToolsPanelStyles): SuperAdminToolsRow[] =>
  Object.entries(details.canConnectTo).map(([service, isOk]) => ({
    label: service,
    value: (
      <span css={isOk ? styles.success : styles.error}>
        {isOk ? 'OK' : 'FAILING'}
        {details.failingCounts[service] ? ` (failed ${pluralize(details.failingCounts[service], 'check')})` : ''}
      </span>
    ),
  }));

/** Super admin view of the server's /api/heartbeat details: overall status, each service connection, and build info. */
export const SuperAdminHealth = ({ styles = DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES }: { styles?: SuperAdminToolsPanelStyles }) => {
  const { details, isFetching, errorInfo, refetch } = useHeartbeatDetails();

  return (
    <div css={styles.panel}>
      <div css={styles.actions}>
        <div css={styles.heading}>{'Server Health'}</div>
        <CommonActionButton onClick={() => refetch()} disabled={isFetching}>{isFetching ? 'Checking...' : 'Refresh'}</CommonActionButton>
      </div>
      {errorInfo && <div css={styles.error}>{errorInfo}</div>}
      {details && (
        <>
          <div css={details.status === 'ok' ? styles.success : styles.error}>{details.message}</div>
          <div css={styles.description}>{`Checked ${formatUnixSecondsAsLocalTime(details.now)}`}</div>
          <SuperAdminToolsRows rows={getServiceRows(details, styles)} styles={styles} />
          <SuperAdminToolsRows rows={getServerRows(details)} styles={styles} />
        </>
      )}
    </div>
  );
};
